import { Tabs, TabsProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PatientInformationHeader from '../../components/Patient/PatientInformationHeader';
import {
    useGetPatientDetailsQuery,
    useGetPatientOrdersQuery,
    useUpdateAllPatientOrdersMutation,
} from '../../features/patient/patientService';
import RoundedButton from '../../components/RoundedButton';
import { useState } from 'react';
import ConfirmationModal from '../../components/Orders/ConfirmationModal';
import { LoadingSpinner } from '../../components/Spinner/LoadingSpinner';
import { OrderStatusEnum } from '../../models/PatientModels';
import PageTitle from '../../components/PageTitle';
import RenderSideItem from '../../components/RenderSideItem';
import OrderBodyMessage from '../../components/Orders/OrderBodyMessage';
import OrderList from '../../components/Orders/OrderList';
import OrderSetList from '../../components/OrderSets/OrderSetList';
import ScriptSureIframe from '../../components/ScriptSure/ScriptSureIframe';
import ScriptSureIframeLayout from '../../components/ScriptSure/ScriptSureIframeLayout';

const items: TabsProps['items'] = [
    {
        key: '1',
        label: 'Medication Queue',
        children: <OrderList />,
    },
    {
        key: '2',
        label: 'Comfort Kits/ Order Sets',
        children: <ScriptSureIframeLayout 
        getHelpModalOpen={false} 
        setGetHelpModalOpen={() => {}}
        scriptSureSource={(
            webAppUrl: string,
            scriptSurePatientId: number,
            scriptSureSessionToken: string,
        ) =>
            `${webAppUrl}/chart/${scriptSurePatientId}/drug-list?orderSet=true&sessiontoken=${scriptSureSessionToken}&darkmode=off"`
        }
    />
    },
    {
        key: '3',
        label: 'Favorites',
        children: (
            <OrderBodyMessage>
                Favorites will be available soon. Stay tuned!
            </OrderBodyMessage>
        ),
    },
];

const AddNewMedication = () => {
    const navigate = useNavigate();
    const { id: patientId = '' } = useParams();

    const [isModalOpen, setIsModalOpen] = useState(false);

    const { data: patientDetails } = useGetPatientDetailsQuery({
        id: patientId,
    });
    const [updateAllPatientOrders, { isLoading: isUpdatingAllPatientOrders }] =
        useUpdateAllPatientOrdersMutation();
    const { data: patientOrders, isLoading } = useGetPatientOrdersQuery({
        patientId: patientId,
        orderStatus: OrderStatusEnum.Draft,
    });

    const isDisabled =
        isLoading || (patientOrders && patientOrders.length === 0);

    const handleUpdateAllPatientOrders = async () => {
        if (!patientOrders) return;
        const orders = patientOrders.map((order) => order.id);
        const response = await updateAllPatientOrders({
            value: { orderIds: orders },
            patientId,
        });

        if (typeof response === 'object' && !('data' in response)) return;

        const { data } = response;
        if (
            !isUpdatingAllPatientOrders &&
            data &&
            !!data.successfulIds.length
        ) {
            setIsModalOpen(true);
        }
    };

    const [tabKey, setTabKey] = useState("1");

    return (
        <div className="">
            <PageTitle
                title="Add New Medication"
                onClick={() => navigate(`/patient/${patientId}`)}
            />
            <div className="mx-5">
                {patientDetails && (
                    <PatientInformationHeader
                        patient={patientDetails}
                        navigate={() => navigate(-1)}
                    >
                        <RenderSideItem
                            link={`/patient/${patientId}/medications-history`}
                            iconSource="/assets/rx-images/patient-dashboard/med-history.png"
                            title="Medication History"
                        />
                        <RenderSideItem
                            link={`/patient/${patientId}/allergies`}
                            title="Drug Allergies"
                            iconSource="/assets/rx-images/patient-dashboard/patient-allergies.png"
                        />
                    </PatientInformationHeader>
                )}
                <Tabs
                    className="mt-4 order-card-tabs"
                    activeKey={tabKey}
                    items={items}
                    onTabClick={(activeKey, e) => setTabKey(activeKey)}
                    tabBarExtraContent={{
                        right: (
                            <div className="d-flex gap-3">
                                {   tabKey === "1" &&
                                    <RoundedButton
                                        onClick={handleUpdateAllPatientOrders}
                                        className={`fw-semibold ${
                                            !isDisabled
                                                ? 'bg-light-blue text-white fw-semibold'
                                                : ''
                                        }`}
                                        disabled={isDisabled}
                                    >
                                        
                                            <div className="d-flex gap-2">
                                                Continue All to ePrescribe
                                                {isUpdatingAllPatientOrders && (
                                                    <LoadingSpinner
                                                        style={{ color: 'white' }}
                                                    />
                                                )}
                                            </div>
                                        
                                        
                                    </RoundedButton>
                                }
                                <Link
                                    to={`/patient/${patientId}/med-order-create`}
                                >
                                    <RoundedButton
                                        onClick={undefined}
                                        className="d-flex p-2 h-fit shadow bg-light-green text-white"
                                    >
                                        <PlusOutlined
                                            rev={undefined}
                                            className="fs-6"
                                        />
                                    </RoundedButton>
                                </Link>
                            </div>
                        ),
                    }}
                />
                <ConfirmationModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title="Successful!"
                    message="The medication(s) were successfully sent"
                    primaryButtonText="Review Medications"
                    secondaryButtonText="View Medication Queue"
                    onPrimaryButtonLinkTo={`/patient/${patientId}/approve-medications/view`}
                    onSecondaryButtonLinkTo={`/patient/${patientId}/add-new-medication`}
                />
            </div>
        </div>
    );
};

export default AddNewMedication;
